import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '../services/api';

interface LoginTheme {
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    text: string;
  };
  logoUrl?: string;
  cmsName?: string;
  cmsSlug?: string;
}

interface LoginThemeContextType {
  theme: LoginTheme;
  isLoading: boolean;
  error: string | null;
  loadThemeFromSlug: (slug: string) => Promise<void>;
  resetToDefault: () => void;
}

const defaultTheme: LoginTheme = {
  colors: {
    primary: '#3B82F6',
    secondary: '#1E40AF',
    accent: '#F59E0B',
    background: '#F8FAFC',
    text: '#1F2937'
  },
  logoUrl: '',
  cmsName: 'CMS Panel',
  cmsSlug: ''
};

const LoginThemeContext = createContext<LoginThemeContextType | undefined>(undefined);

export const useLoginTheme = () => {
  const context = useContext(LoginThemeContext);
  if (context === undefined) {
    throw new Error('useLoginTheme must be used within a LoginThemeProvider');
  }
  return context;
};

interface LoginThemeProviderProps {
  children: ReactNode;
  slug?: string | null;
}

export const LoginThemeProvider: React.FC<LoginThemeProviderProps> = ({ children, slug }) => {
  const [theme, setTheme] = useState<LoginTheme>(defaultTheme);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Aplicar CSS custom properties quando o tema mudar
  useEffect(() => {
    if (theme.colors) {
      const root = document.documentElement;
      root.style.setProperty('--color-primary', theme.colors.primary);
      root.style.setProperty('--color-secondary', theme.colors.secondary);
      root.style.setProperty('--color-accent', theme.colors.accent);
      root.style.setProperty('--color-background', theme.colors.background);
      root.style.setProperty('--color-text', theme.colors.text);
    }
  }, [theme.colors]);

  // Carregar tema baseado no slug
  const loadThemeFromSlug = async (cmsSlug: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Buscar dados públicos do CMS usando o slug
      const publicData = await apiService.getCmsPublicData(cmsSlug);
      
      if (publicData?.success && publicData?.cms) {
        const cms = publicData.cms;
        const newTheme = {
          colors: cms.colors || defaultTheme.colors,
          logoUrl: cms.logoUrl || '',
          cmsName: cms.name || 'CMS Panel',
          cmsSlug: cmsSlug
        };
        
        setTheme(newTheme);
      } else {
        // Se não encontrar dados públicos, usar tema padrão
        setTheme({
          ...defaultTheme,
          cmsName: cmsSlug || 'CMS Panel',
          cmsSlug: cmsSlug
        });
      }
    } catch (err) {
      console.warn('Não foi possível carregar tema público do CMS, usando tema padrão:', err);
      setTheme({
        ...defaultTheme,
        cmsName: cmsSlug || 'CMS Panel',
        cmsSlug: cmsSlug
      });
      setError(null); // Não mostrar erro para o usuário na tela de login
    } finally {
      setIsLoading(false);
    }
  };

  // Resetar para tema padrão
  const resetToDefault = () => {
    setTheme(defaultTheme);
    setError(null);
  };

  // Carregar tema automaticamente quando o slug mudar
  useEffect(() => {
    if (slug) {
      loadThemeFromSlug(slug);
    } else {
      setTheme(defaultTheme);
    }
  }, [slug]);

  const value: LoginThemeContextType = {
    theme,
    isLoading,
    error,
    loadThemeFromSlug,
    resetToDefault
  };

  return (
    <LoginThemeContext.Provider value={value}>
      {children}
    </LoginThemeContext.Provider>
  );
};
